import type { ReportingComparison } from '../../../types/reporting';

/**
 * Format YYYY-MM-DD as a short local date (e.g. "Jan 5").
 * Parses components directly to avoid timezone shifting.
 */
export const formatShortDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Format a daily tooltip date with a shortened weekday (e.g. "Sat, Jul 1"). */
export const formatDayTooltipDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
};

export const formatMonthYearLabel = (dateStr: string): string => {
    const [year, month] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const formatIsoDate = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

/** Inclusive day count between two YYYY-MM-DD strings. */
export const inclusiveDaysBetween = (startDateStr: string, endDateStr: string): number => {
    const start = parseLocalDate(startDateStr).getTime();
    const end = parseLocalDate(endDateStr).getTime();
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
};

/** Natural end of a bucket, optionally clipped to the selected range end. */
export const getPeriodBucketEndDate = (
    startDateStr: string,
    granularity: string = 'week',
    rangeEndDate?: string,
): string => {
    const start = parseLocalDate(startDateStr);
    const end = new Date(start);

    if (granularity === 'day') {
        return startDateStr;
    }

    if (granularity === 'week') {
        end.setDate(end.getDate() + 6);
    } else if (granularity === 'biweekly') {
        end.setDate(end.getDate() + 13);
    } else if (granularity === 'month') {
        end.setMonth(end.getMonth() + 1, 0);
    } else {
        end.setDate(end.getDate() + 6);
    }

    let endStr = formatIsoDate(end);
    if (rangeEndDate && endStr > rangeEndDate) {
        endStr = rangeEndDate;
    }
    if (endStr < startDateStr) {
        return startDateStr;
    }
    return endStr;
};

/** Bucket start clipped to the selected range start (first partial week/month). */
export const getPeriodBucketDisplayStartDate = (
    bucketStartDateStr: string,
    rangeStartDate?: string,
): string => {
    if (rangeStartDate && bucketStartDateStr < rangeStartDate) {
        return rangeStartDate;
    }
    return bucketStartDateStr;
};

export const formatDateRangeForDisplay = (
    startDateStr: string,
    granularity: string = 'week',
    rangeEndDate?: string,
    rangeStartDate?: string,
): string => {
    if (granularity === 'day') {
        return formatShortDate(startDateStr);
    }

    const displayStart = getPeriodBucketDisplayStartDate(startDateStr, rangeStartDate);
    let endStr = getPeriodBucketEndDate(startDateStr, granularity, rangeEndDate);
    if (endStr < displayStart) {
        endStr = displayStart;
    }
    if (endStr === displayStart) {
        return formatShortDate(displayStart);
    }
    return `${formatShortDate(displayStart)} - ${formatShortDate(endStr)}`;
};

/** Calendar comparison mode can have unequal current/prior day counts. */
export const isCalendarComparison = (comparison?: ReportingComparison): boolean =>
    comparison?.mode === 'calendar';

/** Calendar comparison presets can have unequal current/prior day counts. */
export const isCalendarComparisonPreset = (preset?: string): boolean =>
    preset === 'this_month' || preset === 'this_quarter';

const isCalendarComparisonContext = (
    comparison?: ReportingComparison,
    preset?: string,
): boolean => isCalendarComparison(comparison) || isCalendarComparisonPreset(preset);

/**
 * Prior-period label for tooltips.
 * - Uses backend `priorEndDate` when provided.
 * - For calendar / month buckets without an end date, shows only the prior start
 *   (never invent a span that can bleed past the prior month, e.g. Feb 28 → Mar 1).
 * - Otherwise matches the current bucket's clipped length for rolling week leftovers.
 */
export const formatPriorDateRangeForDisplay = (
    priorStartDateStr: string,
    currentStartDateStr: string,
    granularity: string = 'week',
    currentRangeEndDate?: string,
    priorEndDate?: string,
    preset?: string,
    currentRangeStartDate?: string,
): string => {
    if (granularity === 'day') {
        return formatDayTooltipDate(priorStartDateStr);
    }

    if (priorEndDate) {
        if (priorEndDate === priorStartDateStr) {
            return formatShortDate(priorStartDateStr);
        }
        return `${formatShortDate(priorStartDateStr)} - ${formatShortDate(priorEndDate)}`;
    }

    // Calendar MoM/QoQ and month charts: never invent an equal-length prior end.
    if (isCalendarComparisonContext(undefined, preset) || granularity === 'month') {
        return formatShortDate(priorStartDateStr);
    }

    const displayStart = getPeriodBucketDisplayStartDate(currentStartDateStr, currentRangeStartDate);
    let currentEnd = getPeriodBucketEndDate(currentStartDateStr, granularity, currentRangeEndDate);
    if (currentEnd < displayStart) {
        currentEnd = displayStart;
    }
    const spanDays = inclusiveDaysBetween(displayStart, currentEnd);
    const priorEnd = parseLocalDate(priorStartDateStr);
    priorEnd.setDate(priorEnd.getDate() + (spanDays - 1));
    const priorEndStr = formatIsoDate(priorEnd);

    if (priorEndStr === priorStartDateStr) {
        return formatShortDate(priorStartDateStr);
    }
    return `${formatShortDate(priorStartDateStr)} - ${formatShortDate(priorEndStr)}`;
};

/** X-axis label for reporting time series buckets. */
export const formatPeriodAxisLabel = (
    dateStr: string,
    granularity: string = 'week',
    rangeEndDate?: string,
    rangeStartDate?: string,
): string => {
    if (!dateStr) return '';
    if (granularity === 'day') {
        return formatShortDate(dateStr);
    }
    if (granularity === 'month') {
        return formatMonthYearLabel(dateStr);
    }
    return formatDateRangeForDisplay(dateStr, granularity, rangeEndDate, rangeStartDate);
};

/**
 * Hover period label. Months use clipped exact dates (axis stays month/year);
 * other granularities match the axis date ranges.
 */
export const formatPeriodTooltipLabel = (
    dateStr: string,
    granularity: string = 'week',
    rangeEndDate?: string,
    rangeStartDate?: string,
): string => {
    if (!dateStr) return '';
    if (granularity === 'day') {
        return formatDayTooltipDate(dateStr);
    }
    return formatDateRangeForDisplay(dateStr, granularity, rangeEndDate, rangeStartDate);
};

export interface TimeseriesXAxisLayout {
    angle: number;
    textAnchor: 'middle' | 'end';
    height: number;
    bottom: number;
    fontSize: number;
    /**
     * Passed to Recharts `<XAxis minTickGap={…} />`. Unused when labels are stepped
     * with `interval={0}` + end-aligned hiding; kept for older call sites.
     */
    minTickGap: number;
    /** Gap from the axis line to tick text. Same reserved pad as ranking charts. */
    tickMargin: number;
    /**
     * Show every Nth date label, aligned so the last bucket is always labeled
     * (`(count - 1 - i) % tickStep === 0`). 1 = all, 2 = every other, 3 = every 3rd.
     */
    tickStep: number;
    /** Right-only inset so the last centered date label stays inside the SVG. */
    labelPadX: number;
}

/** Default full-size axis font (px). */
export const AXIS_FONT_MAX = 12;
/** Larger full-size axis font when a chart is shown full screen. */
export const AXIS_FONT_MAX_FULLSCREEN = 14;
/** Max font for rotated ranking / category name labels. */
export const AXIS_FONT_MAX_NAME = 11;
/** Max font for ranking name labels in fullscreen. */
export const AXIS_FONT_MAX_NAME_FULLSCREEN = 15;
/** Legibility floor for rotated name (category) labels. */
export const AXIS_FONT_MIN_NAME = 8;
/** Legibility floor for date labels (allowed smaller since dates are never truncated). */
export const AXIS_FONT_MIN_DATE = 6;

/** Approximate glyph width relative to font size for the axis font. */
const AXIS_CHAR_WIDTH_RATIO = 0.6;

const RANKING_AXIS_ANGLE = 45;
/** Space from the axis line to labels. Passed to Recharts `tickMargin` and the band height. */
export const RANKING_AXIS_TICK_MARGIN = 6;
/** Reserved px under labels, inside the x-axis band. Never shrinks. */
const RANKING_AXIS_LABEL_PAD = 6;
/** Recharts `margin.bottom` under the band. Pad already reserves C; don't add a second footer. */
const RANKING_AXIS_MARGIN_BOTTOM = 0;
const RANKING_AXIS_BAND_MAX = 64;
const RANKING_AXIS_BAND_MAX_FULLSCREEN = 130;
/** Domain tab ranking charts are taller; allow more of the name before truncating. */
export const DOMAIN_RANKING_AXIS_BAND_MAX = 80;

let cachedScrollbarThickness: number | undefined;

/** Native scrollbar thickness (horizontal bar height / vertical bar width). Overlay bars are 0. */
export function getRankingScrollbarGutterPx(): number {
    if (cachedScrollbarThickness !== undefined) return cachedScrollbarThickness;
    if (typeof document === 'undefined') {
        cachedScrollbarThickness = 0;
        return 0;
    }
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;width:100px;height:100px;overflow:scroll;';
    document.body.appendChild(probe);
    const thickness = Math.max(probe.offsetWidth - probe.clientWidth, probe.offsetHeight - probe.clientHeight);
    document.body.removeChild(probe);
    cachedScrollbarThickness = thickness;
    return thickness;
}

export function needsRankingScrollbarGutter(contentWidthPx: number, containerWidthPx: number): boolean {
    return containerWidthPx > 0 && contentWidthPx > containerWidthPx;
}

export interface RankingXAxisLayout {
    fontSize: number;
    height: number;
    bottom: number;
}

export interface RotatedXAxisOptions {
    isFullscreen: boolean;
    /** Absolute rotation used for drop math (0 = flat). */
    angleDeg?: number;
    fontSize?: number;
    minFont?: number;
    /** When true, long names ellipsize to keep the reserved pad. Dates should pass false. */
    truncate?: boolean;
    bandMin?: number;
    bandMax?: number;
}

interface AxisLabelBox {
    width: number;
    height: number;
}

let axisMeasureText: SVGTextElement | null | undefined;
let axisMeasureFamily: string | undefined;
let axisMeasureFontPx: number | undefined;

/** Hidden SVG <text> — same drawing model as Recharts axis ticks. */
function getAxisMeasureText(): SVGTextElement | null {
    if (axisMeasureText !== undefined) return axisMeasureText;
    if (typeof document === 'undefined') {
        axisMeasureText = null;
        return null;
    }
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '1');
    svg.setAttribute('height', '1');
    svg.style.cssText = 'position:absolute;left:-9999px;top:0;overflow:visible;pointer-events:none;';
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '0');
    text.setAttribute('y', '0');
    svg.appendChild(text);
    document.body.appendChild(svg);
    axisMeasureText = text;
    return text;
}

function applyAxisMeasureFont(text: SVGTextElement, fontPx: number) {
    if (axisMeasureFamily === undefined) {
        axisMeasureFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
    }
    if (axisMeasureFontPx === fontPx) return;
    text.setAttribute('font-size', `${fontPx}px`);
    text.setAttribute('font-family', axisMeasureFamily);
    text.setAttribute('font-weight', '400');
    axisMeasureFontPx = fontPx;
}

/** Measured unrotated box of an axis tick. SVG getBBox when available; else a width/height guess. */
export function measureAxisLabelBox(label: string, fontPx: number): AxisLabelBox {
    if (!label) return { width: 0, height: 0 };
    const text = getAxisMeasureText();
    if (!text) {
        return { width: label.length * fontPx * AXIS_CHAR_WIDTH_RATIO, height: fontPx };
    }
    applyAxisMeasureFont(text, fontPx);
    text.textContent = label;
    const box = text.getBBox();
    return {
        width: box.width,
        height: box.height > 0 ? box.height : fontPx,
    };
}

function rankingAxisAngleParts(angleDeg = RANKING_AXIS_ANGLE) {
    const rad = (angleDeg * Math.PI) / 180;
    // Do not coerce sin(0) → 1. Flat ticks must use drop = glyph height, not label width.
    return { sin: Math.sin(rad), cos: Math.cos(rad) };
}

/** Rotated AABB height: measured width × sin + measured height × cos. */
function rotatedLabelDrop(box: AxisLabelBox, angleDeg = RANKING_AXIS_ANGLE): number {
    const { sin, cos } = rankingAxisAngleParts(angleDeg);
    return Math.max(0, box.width) * sin + Math.max(0, box.height) * cos;
}

function resolveRotatedAxisGeometry(options: RotatedXAxisOptions) {
    const { isFullscreen } = options;
    const angleDeg = Math.abs(options.angleDeg ?? RANKING_AXIS_ANGLE);
    const fontSize = options.fontSize
        ?? (isFullscreen ? AXIS_FONT_MAX_NAME_FULLSCREEN : AXIS_FONT_MAX_NAME);
    const minFont = options.minFont
        ?? (options.truncate === false ? AXIS_FONT_MIN_DATE : AXIS_FONT_MIN_NAME);
    const bandMax = options.bandMax
        ?? (isFullscreen ? RANKING_AXIS_BAND_MAX_FULLSCREEN : RANKING_AXIS_BAND_MAX);
    const bandMin = options.bandMin ?? 0;
    const maxRotated = Math.max(0, bandMax - RANKING_AXIS_TICK_MARGIN - RANKING_AXIS_LABEL_PAD);
    return { angleDeg, fontSize, minFont, bandMax, bandMin, maxRotated };
}

function bandHeightForLabelBox(box: AxisLabelBox, angleDeg: number): number {
    return Math.ceil(RANKING_AXIS_TICK_MARGIN + rotatedLabelDrop(box, angleDeg) + RANKING_AXIS_LABEL_PAD);
}

function labelFitsBand(label: string, fontPx: number, maxRotated: number, angleDeg: number): boolean {
    return rotatedLabelDrop(measureAxisLabelBox(label, fontPx), angleDeg) <= maxRotated;
}

function truncateAxisNameToFit(
    name: string,
    fontPx: number,
    maxRotated: number,
    angleDeg: number,
): string {
    if (!name) return '';
    if (labelFitsBand(name, fontPx, maxRotated, angleDeg)) return name;
    let lo = 1;
    let hi = name.length;
    let best = 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const candidate = `${name.substring(0, mid)}...`;
        if (labelFitsBand(candidate, fontPx, maxRotated, angleDeg)) {
            best = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return `${name.substring(0, best)}...`;
}

/** Truncate a ranking name so tick margin + rotated box + label pad fit in the max band. */
export function truncateRankingAxisName(name: string, isFullscreen: boolean, bandMax?: number): string {
    const { fontSize, maxRotated, angleDeg } = resolveRotatedAxisGeometry({ isFullscreen, bandMax });
    return truncateAxisNameToFit(name, fontSize, maxRotated, angleDeg);
}

/**
 * Pad-first x-axis band: reserve tick margin + bottom pad, then fit letters.
 * Names truncate; dates shrink font instead.
 */
export function getRotatedXAxisLayout(
    labels: string[],
    options: RotatedXAxisOptions,
): RankingXAxisLayout & { tickMargin: number } {
    const geometry = resolveRotatedAxisGeometry(options);
    let { fontSize } = geometry;
    const { angleDeg, minFont, bandMax, bandMin, maxRotated } = geometry;
    const truncate = options.truncate !== false;

    let displayLabels = labels;
    if (truncate) {
        displayLabels = labels.map((label) => truncateAxisNameToFit(label, fontSize, maxRotated, angleDeg));
    } else {
        while (fontSize > minFont) {
            const size = fontSize;
            const overflow = displayLabels.some(
                (label) => !labelFitsBand(label, size, maxRotated, angleDeg),
            );
            if (!overflow) break;
            fontSize -= 1;
        }
    }

    let tallest = RANKING_AXIS_TICK_MARGIN;
    for (const label of displayLabels) {
        tallest = Math.max(tallest, bandHeightForLabelBox(measureAxisLabelBox(label, fontSize), angleDeg));
    }
    const height = Math.min(bandMax, Math.max(tallest, bandMin));
    return { fontSize, height, bottom: RANKING_AXIS_MARGIN_BOTTOM, tickMargin: RANKING_AXIS_TICK_MARGIN };
}

/** Ranking x-axis: band = tick margin + measured rotated label + label pad. */
export function getRankingXAxisLayout(
    labels: string[],
    isFullscreen: boolean,
    bandMax?: number,
): RankingXAxisLayout {
    const { fontSize, height, bottom } = getRotatedXAxisLayout(labels, { isFullscreen, bandMax });
    return { fontSize, height, bottom };
}

/** Chart left margin (0, flush like ranking) + right margin (10). */
export const TIMESERIES_X_AXIS_SIDE_MARGINS_PX = 10;
/** Y-axis width (55) + side margins. Widget default. */
export const TIMESERIES_X_AXIS_CHROME_PX = 65;

/** Plot width available for category ticks given the outer chart container width. */
export const getTimeseriesPlotWidth = (containerWidthPx: number, yAxisWidthPx = 55): number =>
    Math.max(0, containerWidthPx - yAxisWidthPx - TIMESERIES_X_AXIS_SIDE_MARGINS_PX);

/**
 * Minimum gap between date ticks for Recharts `preserveEnd` skipping.
 * Default Recharts value is 5; we raise it so labels drop earlier on small graphs.
 */
export function getTimeseriesMinTickGap(plotWidthPx?: number, maxLabelLen = 0): number {
    let gap = 8;
    if (plotWidthPx == null || plotWidthPx <= 0) gap = 16;
    else if (plotWidthPx < 280) gap = 24;
    else if (plotWidthPx < 420) gap = 18;
    else if (plotWidthPx < 600) gap = 12;
    // Longer week-range labels need more gap so adjacent ticks don't collide when flat.
    if (maxLabelLen > 10) {
        gap = Math.min(32, gap + (maxLabelLen - 10));
    }
    return gap;
}

/** Extra px between neighboring date labels when deciding skip step. */
const TIMESERIES_TICK_SLOT_GAP_PX = 8;

/** True when this index is on the end-aligned cadence (`tickStep` back from the last point). */
export function isEndAlignedTimeseriesTick(index: number, count: number, tickStep: number): boolean {
    if (count <= 0 || tickStep <= 0) return false;
    if (index < 0 || index >= count) return false;
    return (count - 1 - index) % tickStep === 0;
}

/** Smallest step (1, 2, 3, …) whose end-aligned labels fit in the plot width. */
export function resolveEndAlignedTickStep(
    count: number,
    slotPx: number,
    plotWidthPx?: number,
): number {
    if (count <= 1) return 1;
    if (plotWidthPx == null || plotWidthPx <= 0) return 1;
    const safeSlot = Math.max(1, slotPx);
    for (let k = 1; k <= count; k++) {
        if (Math.ceil(count / k) * safeSlot <= plotWidthPx) return k;
    }
    return count;
}

function timeseriesTickSlotPx(label: string, fontPx: number, angleDeg: number): number {
    const box = measureAxisLabelBox(label || 'M', fontPx);
    const rad = (Math.abs(angleDeg) * Math.PI) / 180;
    return box.width * Math.cos(rad) + box.height * Math.sin(rad) + TIMESERIES_TICK_SLOT_GAP_PX;
}

function maxTimeseriesTickSlotPx(
    labels: string[],
    fontPx: number,
    angleDeg: number,
    fallbackLen: number,
): number {
    if (labels.length === 0) {
        return timeseriesTickSlotPx('M'.repeat(Math.max(1, fallbackLen)), fontPx, angleDeg);
    }
    let max = 0;
    for (const label of labels) {
        max = Math.max(max, timeseriesTickSlotPx(label, fontPx, angleDeg));
    }
    return max;
}

function timeseriesProjectedLabelWidth(label: string, fontPx: number, angleDeg: number): number {
    const box = measureAxisLabelBox(label || 'M', fontPx);
    const rad = (Math.abs(angleDeg) * Math.PI) / 180;
    return box.width * Math.cos(rad) + box.height * Math.sin(rad);
}

function timeseriesLabelPadX(
    labels: string[],
    fontPx: number,
    angleDeg: number,
    fallbackLen: number,
    plotWidthPx?: number,
): number {
    const widest = labels.length > 0
        ? labels.reduce((max, label) => Math.max(max, timeseriesProjectedLabelWidth(label, fontPx, angleDeg)), 0)
        : timeseriesProjectedLabelWidth('M'.repeat(Math.max(1, fallbackLen)), fontPx, angleDeg);
    const half = Math.ceil(widest / 2);
    const cap = plotWidthPx != null && plotWidthPx > 0 ? Math.floor(plotWidthPx * 0.22) : half;
    return Math.max(0, Math.min(half, cap));
}

/** True when a date tick’s text would draw past the SVG left/right edge. */
export function timeseriesTickOverflowsPlot(
    x: number,
    label: string,
    fontPx: number,
    angleDeg: number,
    textAnchor: 'middle' | 'end',
    svgWidth: number,
): boolean {
    if (!(svgWidth > 0) || !Number.isFinite(x)) return false;
    const width = timeseriesProjectedLabelWidth(label, fontPx, angleDeg);
    const left = textAnchor === 'end' ? x - width : x - width / 2;
    const right = textAnchor === 'end' ? x : x + width / 2;
    return left < 2 || right > svgWidth - 2;
}

const TIMESERIES_AXIS_ANGLES = [0, 25] as const;

/**
 * Timeseries date-axis layout:
 * Ladder until labels fit, always keeping the last bucket:
 * all flat → all 25° → every other flat → every other 25° → 1/3 flat → …
 * Pad first (tick margin + bottom pad); dates shrink font, never truncate.
 * Shared by Overview / Invoicing / Domain.
 */
export const getTimeseriesXAxisLayout = (
    granularity: string = 'week',
    dataPointCount: number,
    maxLabelLen = 0,
    isFullscreen = false,
    /** Measured plot width — enables flat labels when they fit; scales font on small charts. */
    plotWidthPx?: number,
    /** Actual tick strings — used to measure the reserved pad-first band. */
    labels: string[] = [],
): TimeseriesXAxisLayout => {
    const baseMaxFont = isFullscreen ? AXIS_FONT_MAX_FULLSCREEN : AXIS_FONT_MAX;
    // Narrow widgets get a lower font ceiling so labels scale with chart size.
    let maxFont = baseMaxFont;
    if (!isFullscreen && plotWidthPx != null && plotWidthPx > 0) {
        if (plotWidthPx < 280) maxFont = Math.max(AXIS_FONT_MIN_DATE, baseMaxFont - 3);
        else if (plotWidthPx < 420) maxFont = Math.max(AXIS_FONT_MIN_DATE, baseMaxFont - 2);
        else if (plotWidthPx < 600) maxFont = Math.max(AXIS_FONT_MIN_DATE, baseMaxFont - 1);
    }
    const labelLen = maxLabelLen
        || labels.reduce((max, label) => Math.max(max, label.length), 0)
        || (granularity === 'biweekly' ? 16 : granularity === 'week' ? 15 : 12);
    let minTickGap = getTimeseriesMinTickGap(plotWidthPx, labelLen);
    if (dataPointCount > 12) {
        minTickGap = Math.min(32, minTickGap + 4);
    }

    const measureLabels = labels.length > 0
        ? labels
        : [ 'M'.repeat(Math.max(1, labelLen)) ];

    const padFor = (angleDeg: number, font: number) => timeseriesLabelPadX(
        measureLabels,
        font,
        angleDeg,
        labelLen,
        plotWidthPx,
    );

    const cadenceFits = (tickStep: number, angleDeg: number, font: number): boolean => {
        if (dataPointCount <= 1) return true;
        if (plotWidthPx == null || plotWidthPx <= 0) return tickStep === 1 && angleDeg === 0;
        const pad = padFor(angleDeg, font);
        const usableWidth = Math.max(0, plotWidthPx - pad);
        const slot = maxTimeseriesTickSlotPx(measureLabels, font, angleDeg, labelLen);
        return Math.ceil(dataPointCount / tickStep) * slot <= usableWidth;
    };

    const applyPadFirst = (angleDeg: number, startFont: number, tickStep: number): TimeseriesXAxisLayout => {
        const useFlat = angleDeg === 0;
        const measured = getRotatedXAxisLayout(measureLabels, {
            isFullscreen,
            angleDeg,
            fontSize: startFont,
            minFont: Math.max(AXIS_FONT_MIN_DATE, 10),
            truncate: false,
            bandMin: 0,
            bandMax: useFlat
                ? (isFullscreen ? 64 : 56)
                : (isFullscreen ? 120 : 80),
        });
        return {
            angle: useFlat ? 0 : -angleDeg,
            textAnchor: useFlat ? 'middle' : 'end',
            height: measured.height,
            bottom: measured.bottom,
            fontSize: measured.fontSize,
            minTickGap,
            tickMargin: measured.tickMargin,
            tickStep,
            labelPadX: padFor(angleDeg, measured.fontSize),
        };
    };

    if (dataPointCount === 0) {
        return applyPadFirst(0, maxFont, 1);
    }

    const maxStep = Math.max(1, dataPointCount);
    for (let k = 1; k <= maxStep; k++) {
        for (const angleDeg of TIMESERIES_AXIS_ANGLES) {
            if (cadenceFits(k, angleDeg, maxFont)) return applyPadFirst(angleDeg, maxFont, k);
        }
    }
    return applyPadFirst(25, maxFont, maxStep);
};

// Helper to check if a date represents the current incomplete period
export const isPeriodCurrent = (dateStr: string, granularity: string = 'week'): boolean => {
    if (!dateStr) return false;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const now = new Date();
    
    // Clear time part of now for accurate day comparison
    now.setHours(0, 0, 0, 0);

    if (granularity === 'day') {
        return date.getTime() === now.getTime();
    }
    
    if (granularity === 'week') {
        const diffTime = now.getTime() - date.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);
        return diffDays >= 0 && diffDays < 7;
    }

    if (granularity === 'biweekly') {
        const diffTime = now.getTime() - date.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);
        return diffDays >= 0 && diffDays < 14;
    }
    
    if (granularity === 'month') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    
    return false;
};

/**
 * True when the selected range cuts the bucket short of its natural week/biweek/month
 * start or end. Falls back to "overlaps today" when neither bound is provided.
 */
export const isPartialReportingPeriod = (
    bucketStartDateStr: string,
    granularity: string = 'week',
    rangeEndDate?: string,
    rangeStartDate?: string,
): boolean => {
    if (!bucketStartDateStr || granularity === 'day') return false;

    if (rangeEndDate || rangeStartDate) {
        const naturalEnd = getPeriodBucketEndDate(bucketStartDateStr, granularity);
        if (rangeEndDate && naturalEnd > rangeEndDate) return true;
        if (rangeStartDate && bucketStartDateStr < rangeStartDate) return true;
        return false;
    }

    return isPeriodCurrent(bucketStartDateStr, granularity);
};

/** Hover badge copy for a clipped / incomplete bucket. */
export const getPartialPeriodLabel = (granularity: string = 'week'): string => {
    if (granularity === 'month') return 'Partial month';
    if (granularity === 'biweekly') return 'Partial period';
    if (granularity === 'week') return 'Partial week';
    return 'Partial';
};

/** Offset for the prior bucket start when API does not send priorDate. Comparison mode takes precedence over granularity. */
export const getPriorPeriodStart = (
    dateStr: string,
    granularity: string = 'week',
    comparison?: ReportingComparison,
    /** Reporting preset — used so QTD month bars fall back to −3 months, not −1. */
    preset?: string,
): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const isQuarterCalendar =
        (comparison?.mode === 'calendar' && comparison.period === 'quarter')
        || preset === 'this_quarter';

    if (isQuarterCalendar && (granularity === 'month' || comparison?.mode === 'calendar')) {
        date.setMonth(date.getMonth() - 3);
        return formatIsoDate(date);
    }

    if (comparison?.mode === 'calendar' && comparison.period === 'month') {
        date.setMonth(date.getMonth() - 1);
    } else if (comparison?.mode === 'rolling' && comparison.offsetDays === 7) {
        date.setDate(date.getDate() - 7);
    } else if (granularity === 'day') {
        date.setDate(date.getDate() - 1);
    } else if (granularity === 'week') {
        date.setDate(date.getDate() - 7);
    } else if (granularity === 'biweekly') {
        date.setDate(date.getDate() - 14);
    } else if (granularity === 'month') {
        date.setMonth(date.getMonth() - 1);
    } else {
        date.setDate(date.getDate() - 7);
    }

    return formatIsoDate(date);
};

/**
 * Shift by N calendar months only when the same day-of-month exists in the target month.
 * Returns undefined when the target month is shorter (e.g. March 30 → February).
 */
export const shiftReportingMonthDateStrict = (dateStr: string, months: number): string | undefined => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const cursor = new Date(Date.UTC(year, month - 1 + months, 1));
    const daysInMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0)).getUTCDate();
    if (day > daysInMonth) {
        return undefined;
    }
    cursor.setUTCDate(day);
    return cursor.toISOString().split('T')[0];
};

/**
 * Rolling month charts (last_year, etc.) pair via backend bucket slices — never invent MoM (−1 month).
 */
const requiresBackendPriorDate = (
    granularity: string,
    comparison?: ReportingComparison,
    preset?: string,
): boolean => {
    if (granularity !== 'month') return false;
    if (comparison?.mode === 'rolling') return true;
    return preset === 'last_year'
        || preset === 'last_6_months'
        || preset === 'last_3_months';
};

/**
 * Prefer the backend prior date. For calendar presets and rolling month charts, never invent
 * a prior start when the API omitted one.
 */
export const resolvePriorPeriodStart = (
    dateStr: string,
    granularity: string = 'week',
    comparison?: ReportingComparison,
    priorDate?: string,
    preset?: string,
): string | undefined => {
    if (priorDate) return priorDate;
    if (isCalendarComparisonContext(comparison, preset)) return undefined;
    if (requiresBackendPriorDate(granularity, comparison, preset)) return undefined;
    return getPriorPeriodStart(dateStr, granularity, comparison, preset);
};

/**
 * Resolve a comparable prior period for tooltip display.
 * Returns a date when a real comparison target exists, even if that period had no activity.
 * Returns undefined when no valid comparison exists (e.g. March 30 → February).
 */
export const resolveComparablePriorPeriodStart = (
    dateStr: string,
    granularity: string = 'week',
    comparison?: ReportingComparison,
    priorDate?: string,
    preset?: string,
): string | undefined => {
    if (priorDate) return priorDate;
    if (!dateStr) return undefined;

    if (isCalendarComparisonContext(comparison, preset) && granularity === 'day') {
        const isQuarter =
            (comparison?.mode === 'calendar' && comparison.period === 'quarter')
            || preset === 'this_quarter';
        const monthShift = isQuarter ? -3 : -1;
        return shiftReportingMonthDateStrict(dateStr, monthShift);
    }

    if (isCalendarComparisonContext(comparison, preset)) return undefined;
    if (requiresBackendPriorDate(granularity, comparison, preset)) return undefined;

    return getPriorPeriodStart(dateStr, granularity, comparison, preset);
};

/** Formatted prior-period label for comparison tooltips. */
export const resolveComparablePriorPeriodLabel = (
    dateStr: string,
    granularity: string = 'week',
    comparison?: ReportingComparison,
    priorDate?: string,
    priorEndDate?: string,
    preset?: string,
    periodEndDate?: string,
    periodStartDate?: string,
): string | undefined => {
    const priorStart = resolveComparablePriorPeriodStart(
        dateStr,
        granularity,
        comparison,
        priorDate,
        preset,
    );
    if (!priorStart) return undefined;

    return formatPriorDateRangeForDisplay(
        priorStart,
        dateStr,
        granularity,
        periodEndDate,
        priorEndDate,
        preset,
        periodStartDate,
    );
};
